import React from 'react';

/**
 * EmptyState
 *
 * Every "no results" case in the app previously rendered a red
 * `ri-error-warning-line` inside a table row. That is the wrong signal: having
 * no agents yet is a normal starting condition, not a failure, and painting it
 * red tells users something is broken when nothing is.
 *
 * This component separates the two cases. The default (neutral) variant is for
 * genuinely empty data and leans on the brand tint. `variant="error"` is
 * reserved for actual request failures, where red is warranted.
 *
 * @param icon    remixicon class; pick one that depicts the *content type*
 * @param title   short statement of what is not here
 * @param body    optional one-liner on how to change that
 * @param action  optional CTA node — an empty list is the best place to offer one
 * @param variant "empty" (default) | "error"
 */
export const EmptyState = ({ icon = 'ri-inbox-line', title, body, action, variant = 'empty' }) => (
    <div className={`wr-empty ${variant === 'error' ? 'wr-empty--error' : ''}`}>
        <div className="wr-empty__art">
            <i className={icon} aria-hidden="true" />
        </div>
        <p className="wr-empty__title">{title}</p>
        {body ? <p className="wr-empty__body">{body}</p> : null}
        {action}
    </div>
);

/**
 * Table-row wrapper. The lists in this app render empties inside <tbody>, so the
 * block needs a full-width cell that opts out of the usual row padding, borders
 * and hover tint — otherwise it reads as a data row that happens to be strange.
 *
 * @param colSpan must match the table's column count
 */
export const EmptyRow = ({ colSpan, ...props }) => (
    <tr className="wr-empty-row">
        <td colSpan={colSpan}>
            <EmptyState {...props} />
        </td>
    </tr>
);

export default EmptyState;
